// "Is this a phone-width viewport?" — ONE media query for the whole app, read through an external store.
//
// It used to be a `useState` + `useEffect` per consumer: each mount created its own `matchMedia`, attached its
// own listener, and then set state on mount — a second render of that component for a value that was already
// known. With eight consumers, several of them per-route, that is eight subscriptions and eight extra render
// passes on every route commit, and it showed up in the transition profile as one of the larger app-code frames.
//
// As a store there is exactly one query and one listener for the process, and a component mounting after the
// first read gets the current answer in its FIRST render — no catch-up pass at all.
//
// The server (and the hydration render) still answers `false`, deliberately and unchanged: the viewport is not
// knowable there, and the consumers that care pair this with `useHydrated()` (see the sections). Every layout
// that can be settled in CSS should be — the nav stack's whole geometry is a media query for this reason
// (see components/custom/route-transition/stack) — so reach for this only where the question is answered in JSX.

import { useSyncExternalStore } from 'react'

const MOBILE_BREAKPOINT = 768
/**
 * Exported because a few places need the query IMPERATIVELY rather than as a subscription — the nav stack
 * asks "is this a phone right now?" inside a settle and inside a flag pass, where a hook cannot go. One
 * breakpoint, one string: it must also match the `max-width` of the stack's media query in app.css.
 */
export const MOBILE_QUERY = `(max-width: ${MOBILE_BREAKPOINT - 1}px)`

let query: MediaQueryList | null = null
let matches = false
const listeners = new Set<() => void>()

function onChange(): void {
  const next = query?.matches ?? false
  if (next === matches) return
  matches = next
  for (const l of listeners) l()
}

function subscribe(listener: () => void): () => void {
  // Created on the FIRST subscription, never at module scope: this module is imported into the SSR bundle too,
  // where `window` does not exist.
  if (!query) {
    query = window.matchMedia(MOBILE_QUERY)
    matches = query.matches
    query.addEventListener('change', onChange)
  }
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

/** The live value once anything has subscribed; `false` before that, which is also what the server answers. */
function getSnapshot(): boolean {
  return query ? query.matches : matches
}

export function useIsMobile(): boolean {
  return useSyncExternalStore(subscribe, getSnapshot, () => false)
}
